
import React, { useState, useEffect } from 'react';
import { addDays, subDays } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { formatCalendarDate } from '@/utils/dateHelpers';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { useWorkoutTypes } from '@/hooks/useWorkoutTypes';
import CalendarDatePicker from '@/components/calendar/CalendarDatePicker';
import WorkoutTypeFilters from '@/components/calendar/WorkoutTypeFilters';
import EffectiveTimeSlotsList from '@/components/calendar/EffectiveTimeSlotsList';
import NotesButton from '@/components/calendar/NotesButton';
import DayNotesModal from '@/components/calendar/DayNotesModal';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import { useCalendarFilters } from '@/hooks/useCalendarFilters';
import { useSearchParams } from 'react-router';
import { usePageScrollRestoration } from '@/hooks/usePageScrollRestoration';

const Calendar = () => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [highlightedTimeSlot, setHighlightedTimeSlot] = useState<string | null>(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [searchParams] = useSearchParams();
  
  // Keep last scroll position on Calendar route
  usePageScrollRestoration();

  // Use persistent filters hook
  const {
    selectedDate,
    setSelectedDate,
    selectedWorkoutTypes,
    setSelectedWorkoutTypes,
  } = useCalendarFilters();

  const { workoutTypes, loading: workoutTypesLoading } = useWorkoutTypes();

  // Handle URL parameters for navigation from client details
  useEffect(() => {
    const urlDate = searchParams.get('date');
    const urlTimeSlot = searchParams.get('timeSlot');
    const urlWorkoutType = searchParams.get('workoutType');

    if (urlDate) {
      const parsedDate = new Date(urlDate);
      if (!isNaN(parsedDate.getTime())) {
        setSelectedDate(normalizeDate(parsedDate));
      }
    }

    if (urlWorkoutType) {
      setSelectedWorkoutTypes(new Set([urlWorkoutType]));
    }

    // Scroll to specific timeslot after a short delay to ensure DOM is ready
    if (urlTimeSlot) {
      const scrollToTimeSlot = () => {
        const timeSlotElement = document.querySelector(`[data-timeslot="${urlTimeSlot}"]`);
        if (timeSlotElement) {
          timeSlotElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          return true;
        }
        return false;
      };

      // Try immediate scroll
      if (!scrollToTimeSlot()) {
        // If not found immediately, try with delays
        setTimeout(() => {
          if (!scrollToTimeSlot()) {
            // Try once more with longer delay
            setTimeout(scrollToTimeSlot, 1000);
          }
        }, 500);
      }
    }
  }, [searchParams, setSelectedDate, setSelectedWorkoutTypes]);

  const handlePreviousDay = () => {
    setSelectedDate(prev => subDays(prev, 1));
  };

  const handleNextDay = () => {
    setSelectedDate(prev => addDays(prev, 1));
  };

  // Helper function to normalize dates to start of day in local timezone
  const normalizeDate = (date: Date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  };

  const findNearestTimeSlot = () => {
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes(); // Current time in minutes from midnight
    
    // Get all time slot elements
    const timeSlotElements = document.querySelectorAll('[data-timeslot]');
    let nearestElement = null;
    let smallestDifference = Infinity;
    
    timeSlotElements.forEach((element) => {
      const timeSlotTime = element.getAttribute('data-timeslot');
      if (timeSlotTime) {
        // Parse time slot time (format: "HH:MM")
        const [hours, minutes] = timeSlotTime.split(':').map(Number);
        const slotTimeInMinutes = hours * 60 + minutes;
        
        // Calculate difference (considering time slots that are currently running or upcoming)
        // For ongoing sessions, use the difference from current time
        // For future sessions, use the difference from current time
        const difference = Math.abs(currentTime - slotTimeInMinutes);
        
        // Prefer ongoing or upcoming time slots over past ones
        // If current time is between start and end time, prioritize that slot
        if (difference < smallestDifference) {
          smallestDifference = difference;
          nearestElement = element;
        }
      }
    });
    
    return nearestElement;
  };

  const handleGoToToday = () => {
    setSelectedDate(normalizeDate(new Date()));
    
    // Scroll to nearest time slot after a short delay to ensure DOM is updated
    setTimeout(() => {
      const nearestTimeSlot = findNearestTimeSlot();
      if (nearestTimeSlot) {
        nearestTimeSlot.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        
        // Highlight the nearest time slot after scrolling
        const timeSlotId = nearestTimeSlot.getAttribute('data-timeslot');
        if (timeSlotId) {
          setHighlightedTimeSlot(timeSlotId);
          
          // Remove highlight after 3 seconds
          setTimeout(() => {
            setHighlightedTimeSlot(null);
          }, 3000);
        }
      }
    }, 100);
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(normalizeDate(date));
      setShowDatePicker(false);
    }
  };

  const handleWorkoutTypeFilter = (workoutTypeId: string) => {
    const newSelection = new Set(selectedWorkoutTypes);
    
    if (workoutTypeId === 'all') {
      // Clear all selections and add 'all' to show all workout types
      newSelection.clear();
      newSelection.add('all');
    } else if (newSelection.has(workoutTypeId)) {
      // Remove the specific workout type
      newSelection.delete(workoutTypeId);
      // If no specific types are selected, add 'all' back
      if (newSelection.size === 0 || (newSelection.size === 1 && newSelection.has('all'))) {
        newSelection.clear();
        newSelection.add('all');
      }
    } else {
      // Remove 'all' if it exists and add the specific workout type
      newSelection.delete('all');
      newSelection.add(workoutTypeId);
    }
    
    setSelectedWorkoutTypes(newSelection);
  };

  const isLoading = workoutTypesLoading;

  if (isLoading) {
    return (
      <MainLayout>
        <div className="">
          <div className="flex items-center justify-center h-64">
            <div className="text-lg">{t('common.loading')}</div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Fixed Header */}
      <div className="fixed z-40 bg-background border-b lg:w-[82%] top-[64px] lg:top-0 py-4 w-full">
        <div className="w-full px-4 space-y-4">
          {/* Header with navigation */}
          <div className="flex flex-col space-y-4">
            {/* Only show title on desktop */}
            {!isMobile && (
              <h1 className="text-2xl md:text-3xl font-bold">
                {t('calendar.title')}
              </h1>
            )}
            
            {/* Date Navigation */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-4 w-full">
              <div className="flex items-center gap-1 sm:gap-2">
                <Button
                  variant="outline"
                  size={isMobile ? "sm" : "sm"}
                  onClick={handlePreviousDay}
                  className="flex items-center gap-1 text-xs sm:text-sm"
                >
                  <ChevronLeft className={`h-3 w-3 sm:h-4 sm:w-4`} />
                  <span className="hidden sm:inline">{t('calendar.previousDay')}</span>
                </Button>
                
                <button 
                  onClick={() => setShowDatePicker(true)}
                  className={`font-semibold px-2 sm:px-4 py-1 sm:py-2 bg-muted rounded-md hover:bg-muted/80 transition-colors cursor-pointer ${
                    isMobile ? 'text-sm' : 'text-lg'
                  }`}
                >
                  {formatCalendarDate(selectedDate, i18n.language, isMobile)}
                </button>
                
                <Button
                  variant="outline"
                  size={isMobile ? "sm" : "sm"}
                  onClick={handleNextDay}
                  className="flex items-center gap-1 text-xs sm:text-sm"
                >
                  <span className="hidden sm:inline">{t('calendar.nextDay')}</span>
                  <ChevronRight className={`h-3 w-3 sm:h-4 sm:w-4`} />
                </Button>
              </div>
              
              <div className="flex items-center gap-1 sm:gap-2">
                <Button
                  variant="outline"
                  size={isMobile ? "sm" : "sm"}
                  onClick={handleGoToToday}
                  className={isMobile ? "text-xs px-2" : ""}
                >
                  {t('calendar.today')}
                </Button>
                
                <Button
                  variant="outline"
                  size={isMobile ? "sm" : "sm"}
                  onClick={() => setShowDatePicker(true)}
                  className={`flex items-center gap-1 ${isMobile ? "text-xs px-2" : ""}`}
                >
                  <CalendarIcon className={`${isMobile ? "h-3 w-3" : "h-4 w-4"}`} />
                  <span className="hidden sm:inline truncate max-w-[200px]">{t('calendar.goToDate')}</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Workout Type Filters */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between py-4 w-full">
            <WorkoutTypeFilters
              workoutTypes={workoutTypes}
              selectedWorkoutTypes={selectedWorkoutTypes}
              onWorkoutTypeFilter={handleWorkoutTypeFilter}
            />
            <NotesButton
              date={selectedDate}
              onOpenNotes={() => setShowNotesModal(true)}
            />
          </div>
        </div>
      </div>

      <div className="pt-[24rem] pb-4 md:pb-6 lg:pt-[16rem]">

        {/* Time Slots List */}
        <EffectiveTimeSlotsList
          selectedDate={selectedDate}
          workoutTypes={workoutTypes}
          selectedWorkoutTypes={selectedWorkoutTypes}
          highlightedTimeSlot={highlightedTimeSlot}
        />
      </div>

      {/* Date Picker Modal */}
      <CalendarDatePicker
        open={showDatePicker}
        onOpenChange={setShowDatePicker}
        selectedDate={selectedDate}
        onDateSelect={handleDateSelect}
      />

      <DayNotesModal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        date={selectedDate}
      />
    </MainLayout>
  );
};

export default Calendar;
